import { useQuery } from "@tanstack/react-query";
import useApp from "../hooks/useApp";
import { customersGetChatBotKey, silent, unwrap } from "../api";
import { CustomerFeature, Feature } from "../api/types";

function hasChatBot(customerFeature: CustomerFeature) {
  return customerFeature.features.some((feature) => feature.name === "CHAT_BOT" && feature.enabled);
}

// Deliberately ignores the topbar customer filter: one chat bot customer is enough to enable it everywhere
export default function useChatBotKey() {
  const { appData } = useApp();
  const authFeatures = appData?.auth?.user ? (appData.auth.features ?? []) : [];
  const customerId = authFeatures.find(hasChatBot)?.customer.id;

  const { data, isPending } = useQuery({
    queryKey: ["chat-bot-key", customerId],
    queryFn: () => unwrap(customersGetChatBotKey({ path: { id: customerId as number }, ...silent })),
    enabled: !!customerId,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const enabledFeatures = authFeatures.flatMap((f) => f.features).filter((feature) => feature.enabled);
  const features = [...new Map<string, Feature>(enabledFeatures.map((feature) => [feature.name, feature])).values()];

  return {
    key: customerId ? (data?.key ?? undefined) : undefined,
    isPending: !!customerId && isPending,
    features,
  };
}
